import { Dialog as DialogPrimitive } from "@base-ui/react/dialog";
import { useNavigate } from "@tanstack/react-router";
import { Search } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { SearchBar } from "#/components/SearchBar";
import { ROUTES } from "#/lib/routes";
import { cn } from "#/lib/utils";

export interface SearchDialogProps {
  open: boolean;
  onClose: () => void;
}

export function SearchDialog({ open, onClose }: SearchDialogProps) {
  const [query, setQuery] = useState("");
  const inputRef = useRef<HTMLInputElement>(null);
  const navigate = useNavigate();

  useEffect(() => {
    if (!open) {
      setQuery("");
      return;
    }
    const t = setTimeout(() => inputRef.current?.focus(), 0);
    return () => clearTimeout(t);
  }, [open]);

  return (
    <DialogPrimitive.Root open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogPrimitive.Portal>
        <DialogPrimitive.Backdrop
          className={cn(
            "fixed inset-0 z-[100] bg-foreground/40 backdrop-blur-sm",
            "data-open:animate-in data-closed:animate-out",
            "data-open:fade-in-0 data-closed:fade-out-0",
            "duration-200",
          )}
        />
        <DialogPrimitive.Popup
          aria-describedby={undefined}
          className={cn(
            "fixed z-[100] flex flex-col overflow-hidden border border-border bg-card shadow-2xl outline-none",
            "inset-x-0 bottom-0 max-h-[85vh] w-full rounded-t-2xl",
            "lg:inset-x-auto lg:top-[10vh] lg:bottom-auto lg:left-1/2 lg:-translate-x-1/2",
            "lg:max-h-none lg:w-[calc(100%-2rem)] lg:max-w-2xl lg:rounded-2xl",
            "data-open:animate-in data-closed:animate-out",
            "data-open:fade-in-0 data-closed:fade-out-0",
            "data-open:slide-in-from-bottom data-closed:slide-out-to-bottom",
            "lg:data-open:zoom-in-95 lg:data-closed:zoom-out-95",
            "duration-200 ease-out",
          )}
        >
          <DialogPrimitive.Title className="sr-only">
            Search products
          </DialogPrimitive.Title>
          <SearchBar
            inputRef={inputRef}
            value={query}
            onChange={setQuery}
            onSubmit={(q) => {
              navigate({ to: ROUTES.shop, search: { q, page: 1 } });
              onClose();
            }}
            onSuggestionPicked={onClose}
            placeholder="Search books..."
            idleHint="Start typing to search products"
            suggestionsPosition="inline"
            className="flex h-14 items-center gap-3 border-border border-b px-5"
            inputClassName="flex-1 border-0 bg-transparent text-base text-foreground outline-none placeholder:text-muted-foreground"
            suggestionsClassName="flex-1 overflow-auto lg:max-h-[60vh] lg:flex-none"
            leftSlot={
              <Search className="size-5 shrink-0 text-muted-foreground" />
            }
            rightSlot={
              <kbd className="hidden h-6 items-center rounded-md bg-muted px-2 font-medium text-muted-foreground text-xs sm:inline-flex">
                esc
              </kbd>
            }
          />
        </DialogPrimitive.Popup>
      </DialogPrimitive.Portal>
    </DialogPrimitive.Root>
  );
}
